import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const LOGO_URL =
  'https://public.readdy.ai/ai/img_res/1465fc3d-9fcb-4ff8-a972-fea1715c1e21.png';

const gradeAProduct = {
  id: 1,
  gradeKey: 'Grade A',
  titleKey: 'form_product_a',
  descKey: 'grade_a_desc',
  img: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg',
  badge: 'PREMIUM',
  badgeColor: 'bg-yellow-500 text-black',
  quickSpecs: {
    dimensions: '1200 × 800 mm',
    loadCapacity: 'Up to 1500 kg',
    woodType: 'Hardwood/Softwood',
  },
  qualityBadges: [
    { icon: 'ri-shield-check-line', label: 'EPAL Certified' },
    { icon: 'ri-checkbox-circle-line', label: 'Inspected' },
    { icon: 'ri-store-3-line', label: 'In Stock' },
  ],
  detailImages: [
    'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg',
    'https://public.readdy.ai/ai/img_res/56a8c43826cabf3162b34c61d88bbb8e.jpg',
    'https://readdy.ai/api/search-image?query=stacked%20grade%20A%20euro%20pallets%20neat%20rows%20warehouse%20storage%20facility%20Belgium%20logistics&width=800&height=600&seq=ga3&orientation=landscape',
  ],
  specs: [
    { label: 'Dimensions', value: '1200 × 800 mm (EUR/EPAL)' },
    { label: 'Load Capacity', value: 'Up to 1500 kg dynamic' },
    { label: 'Certification', value: 'EPAL certified' },
    { label: 'Condition', value: 'Excellent — minimal wear' },
    { label: 'Wood Type', value: 'Hardwood / Softwood' },
    { label: 'Repair Status', value: 'No repairs — original condition' },
    { label: 'Availability', value: 'In stock — large quantities' },
    { label: 'Min. Order Qty', value: 'Contact us for details' },
  ],
  features: [
    'Minimal wear & tear',
    'EPAL certified',
    'Structurally sound',
    'Ready for immediate use',
  ],
  fullDesc:
    'Our Grade A used Euro pallets represent the highest quality in the second-hand pallet market. Each pallet is individually hand‑inspected by our trained staff to ensure it meets strict EPAL standards.',
  pricing: [
    { range: '10-100', price: 14, unit: '€/pallet' },
    { range: '100-500', price: 12, unit: '€/pallet' },
    { range: '500-5000', price: 10.5, unit: '€/pallet' },
    { range: '5000+', price: 9.5, unit: '€/pallet' },
  ],
};

function OrderModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    product: t('form_product_a'),
    quantity: '1',
    deliveryAddress: '',
    preferredDate: '',
    notes: '',
  });
  const [orderStatus, setOrderStatus] = useState<
    'form' | 'summary' | 'sending' | 'success'
  >('form');

  // Prevent background scrolling while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    // Limit notes length to 500 characters
    if (name === 'notes' && value.length > 500) return;
    setOrderData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    setOrderStatus('summary');
  };

  // -----------------------------------------------------------------------
  //  Submit order – now includes robust error handling & timeout safeguard
  // -----------------------------------------------------------------------
  const handleSubmitOrder = async () => {
    setOrderStatus('sending');
    try {
      const body = new URLSearchParams();
      Object.entries(orderData).forEach(([k, v]) => body.append(k, v));

      // Timeout wrapper (10 seconds) – prevents hanging fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10_000);

      const res = await fetch(
        'https://readdy.ai/api/form/d6idbb857726vudt88hg',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: body.toString(),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!res.ok) {
        // Show the server‑provided error if any, otherwise generic message
        const errMsg = (await res.text()) || t('order_submit_error');
        console.error('Order submission failed:', errMsg);
        setOrderStatus('form');
        return;
      }

      setOrderStatus('success');
    } catch (error) {
      console.error('Order submission exception:', error);
      // Keep the user on the form so they can retry
      setOrderStatus('form');
    }
  };

  // Unique (yet reproducible) reference number for the order
  const referenceNumber: string = `QUO-${Date.now()
    .toString()
    .slice(-8)}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full border border-gray-200 text-gray-600 hover:text-gray-900 transition-all cursor-pointer shadow-md"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="p-8">
          {/* ------------------------------------------------- FORM ------------------------------------------------- */}
          {orderStatus === 'form' && (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-full">
                    <i className="ri-shopping-cart-line text-black text-lg"></i>
                  </div>
                  <h2 className="text-3xl font-black text-gray-900">
                    {t('order_title')}
                  </h2>
                </div>
                <p className="text-gray-600 text-sm">{t('order_subtitle')}</p>
              </div>

              <form data-readdy-form onSubmit={handleContinue} className="space-y-5">
                {/* Company & Contact */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                      {t('order_company')} *
                    </label>
                    <input
                      name="companyName"
                      required
                      value={orderData.companyName}
                      onChange={handleChange}
                      placeholder={t('order_company_ph')}
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                      {t('order_contact')} *
                    </label>
                    <input
                      name="contactName"
                      required
                      value={orderData.contactName}
                      onChange={handleChange}
                      placeholder={t('order_contact_ph')}
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                    />
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                      {t('form_email')} *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={orderData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                      {t('form_phone')} *
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      value={orderData.phone}
                      onChange={handleChange}
                      placeholder="+32 ..."
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                    />
                  </div>
                </div>

                {/* Quantity & Preferred date */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                      {t('order_quantity')} *
                    </label>
                    <input
                      name="quantity"
                      type="number"
                      required
                      min="1"
                      value={orderData.quantity}
                      onChange={handleChange}
                      placeholder={t('order_quantity_ph')}
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                      {t('order_date')}
                    </label>
                    <input
                      name="preferredDate"
                      type="date"
                      value={orderData.preferredDate}
                      onChange={handleChange}
                      className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all cursor-pointer"
                    />
                  </div>
                </div>

                {/* Delivery address */}
                <div>
                  <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                    {t('order_address')} *
                  </label>
                  <input
                    name="deliveryAddress"
                    required
                    value={orderData.deliveryAddress}
                    onChange={handleChange}
                    placeholder={t('order_address_ph')}
                    className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-gray-700 text-xs tracking-widest uppercase mb-2 font-bold">
                    {t('order_notes')}
                  </label>
                  <textarea
                    name="notes"
                    value={orderData.notes}
                    onChange={handleChange}
                    maxLength={500}
                    rows={3}
                    placeholder={t('order_notes_ph')}
                    className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all resize-none"
                  ></textarea>
                  <div className="text-right text-gray-500 text-xs mt-1">
                    {orderData.notes.length}/500
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase"
                  >
                    {t('order_cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md"
                  >
                    {t('order_review')}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ------------------------------------------------- SUMMARY ------------------------------------------------- */}
          {orderStatus === 'summary' && (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-full">
                    <i className="ri-file-list-3-line text-black text-lg"></i>
                  </div>
                  <h2 className="text-3xl font-black text-gray-900">
                    {t('order_summary_title')}
                  </h2>
                </div>
                <p className="text-gray-600 text-sm">{t('order_summary_subtitle')}</p>
              </div>

              <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 mb-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Company info */}
                  <div>
                    <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-4">
                      {t('order_company_info')}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <div className="text-gray-500 text-xs mb-1">
                          {t('order_company_name_label')}
                        </div>
                        <div className="text-gray-900 font-bold">
                          {orderData.companyName}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">
                          {t('order_contact_label')}
                        </div>
                        <div className="text-gray-900 font-bold">
                          {orderData.contactName}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">
                          {t('order_email_label')}
                        </div>
                        <div className="text-gray-900 font-bold break-all">
                          {orderData.email}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">
                          {t('order_phone_label')}
                        </div>
                        <div className="text-gray-900 font-bold">{orderData.phone}</div>
                      </div>
                    </div>
                  </div>

                  {/* Order details */}
                  <div>
                    <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-4">
                      {t('order_details')}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <div className="text-gray-500 text-xs mb-1">
                          {t('order_product_label')}
                        </div>
                        <div className="text-gray-900 font-bold">{orderData.product}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">
                          {t('order_qty_label')}
                        </div>
                        <div className="text-gray-900 font-bold">
                          {orderData.quantity} {t('order_qty_unit')}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">
                          {t('order_address_label')}
                        </div>
                        <div className="text-gray-900 font-bold">
                          {orderData.deliveryAddress}
                        </div>
                      </div>
                      {orderData.preferredDate && (
                        <div>
                          <div className="text-gray-500 text-xs mb-1">
                            {t('order_date_label')}
                          </div>
                          <div className="text-gray-900 font-bold">
                            {orderData.preferredDate}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Optional notes */}
                {orderData.notes && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="text-gray-500 text-xs mb-2">
                      {t('order_notes_label')}
                    </div>
                    <div className="text-gray-900 text-sm">{orderData.notes}</div>
                  </div>
                )}
              </div>

              {/* Helper text */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-yellow-600 text-lg mt-0.5"></i>
                  <div className="text-sm text-gray-700">
                    <strong className="font-bold">{t('order_next_steps')}</strong>{' '}
                    {t('order_next_steps_desc')}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setOrderStatus('form')}
                  className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  {t('order_edit')}
                </button>
                <button
                  onClick={handleSubmitOrder}
                  className="flex-1 px-6 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md"
                >
                  {t('order_confirm')}
                </button>
              </div>
            </>
          )}

          {/* ------------------------------------------------- SENDING ------------------------------------------------- */}
          {orderStatus === 'sending' && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              <h4 className="text-gray-900 font-black text-xl mb-2">
                {t('order_submitting')}
              </h4>
              <p className="text-gray-600 text-sm">{t('order_submitting_desc')}</p>
            </div>
          )}

          {/* ------------------------------------------------- SUCCESS ------------------------------------------------- */}
          {orderStatus === 'success' && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-50 rounded-full border-4 border-green-200 mb-6">
                <i className="ri-check-line text-green-600 text-4xl"></i>
              </div>
              <h4 className="text-gray-900 font-black text-2xl mb-3">
                {t('order_success_title')}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-3 mb-6">
                <div className="text-gray-500 text-xs tracking-widest uppercase mb-1">
                  {t('order_ref_label')}
                </div>
                <div className="text-gray-900 font-black text-xl">{referenceNumber}</div>
              </div>
              <p className="text-gray-600 text-sm max-w-md mb-8 leading-relaxed">
                {t('order_success_desc')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md"
                >
                  {t('order_close')}
                </button>
                <a
                  href="tel:+32487261970"
                  className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase"
                >
                  <i className="ri-phone-line mr-2"></i>
                  {t('product_call_us')}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GradeAPage() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [showOrderModal, setShowOrderModal] = useState(false);

  // Add SEO metadata dynamically
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    document.title = 'Grade A Used Euro Pallets Antwerp | EPAL Certified | PEL Partner Belgium';

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (el) { el.setAttribute('content', value); }
      else {
        el = document.createElement('meta');
        el.setAttribute(attr === 'content' ? 'name' : attr, selector.match(/\[(?:name|property)="([^"]+)"\]/)?.[1] ?? '');
        el.setAttribute('content', value);
        document.head.appendChild(el);
      }
    };

    setMeta('meta[name="description"]', 'name', 'Premium Grade A used Euro pallets in Antwerp, Belgium. EPAL certified, minimal wear, food-grade ready. Prices from €7.5/pallet. Fast delivery across Belgium. Contact +32 487 26 19 70.');
    setMeta('meta[name="keywords"]', 'name', 'Grade A pallets, EPAL certified pallets, premium euro pallets, used pallets Antwerp, food-grade pallets Belgium, quality pallets');
    setMeta('meta[name="robots"]', 'name', 'index, follow');

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    setOg('og:title', 'Grade A Used Euro Pallets Antwerp | EPAL Certified | PEL Partner Belgium');
    setOg('og:description', 'Premium Grade A used Euro pallets in Antwerp, Belgium. EPAL certified, minimal wear, food-grade ready. Prices from €7.5/pallet.');
    setOg('og:url', `${siteUrl}/pallets/grade-a`);
    setOg('og:type', 'product');
    setOg('og:image', 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg');

    const setTwitter = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    setTwitter('twitter:card', 'summary_large_image');
    setTwitter('twitter:title', 'Grade A Used Euro Pallets Antwerp | EPAL Certified | PEL Partner');
    setTwitter('twitter:description', 'Premium Grade A used Euro pallets in Antwerp, Belgium. EPAL certified, minimal wear, food-grade ready. Prices from €7.5/pallet.');
    setTwitter('twitter:image', 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${siteUrl}/pallets/grade-a`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${siteUrl}/pallets/grade-a`);
      document.head.appendChild(canonical);
    }

    // Schema.org JSON-LD
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Grade A Used Euro Pallets',
      description: 'Premium EPAL-certified Grade A used Euro pallets. Minimal wear, food-grade ready, structurally sound. Available in large quantities from Antwerp, Belgium.',
      image: 'https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg',
      url: `${siteUrl}/pallets/grade-a`,
      brand: { '@type': 'Brand', name: 'PEL Partner' },
      category: 'Industrial Pallets',
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Dimensions', value: '1200 × 800 mm' },
        { '@type': 'PropertyValue', name: 'Load Capacity', value: 'Up to 1500 kg' },
        { '@type': 'PropertyValue', name: 'Certification', value: 'EPAL Certified' },
        { '@type': 'PropertyValue', name: 'Condition', value: 'Excellent — minimal wear' },
      ],
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'EUR',
        lowPrice: '9.5',
        highPrice: '14',
        offerCount: '4',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'PEL Partner', url: siteUrl },
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Products', item: `${siteUrl}/#used-pallets` },
        { '@type': 'ListItem', position: 3, name: 'Grade A Pallets', item: `${siteUrl}/pallets/grade-a` },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Grade A Used Euro Pallets – PEL Partner Antwerp',
      description: 'Premium EPAL-certified Grade A used Euro pallets from Antwerp, Belgium. Prices from €7.5/pallet.',
      url: `${siteUrl}/pallets/grade-a`,
      inLanguage: 'en',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'PEL Partner' },
      breadcrumb: breadcrumbSchema,
    };

    const schemas = [productSchema, breadcrumbSchema, webPageSchema];
    const scriptEls: HTMLScriptElement[] = [];
    schemas.forEach((schema, i) => {
      const existing = document.getElementById(`schema-grade-a-${i}`);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      script.id = `schema-grade-a-${i}`;
      document.head.appendChild(script);
      scriptEls.push(script);
    });

    return () => { scriptEls.forEach((s) => { if (s.parentNode) s.parentNode.removeChild(s); }); };
  }, []);

  // -----------------------------------------------------------------------
  //  Helper: calculate the correct price tier based on quantity
  // -----------------------------------------------------------------------
  const getCurrentPricing = (qty: number) => {
    const tier = gradeAProduct.pricing.find((p) => {
      if (p.range.includes('+')) {
        const min = parseInt(p.range.replace('+', ''), 10);
        return qty >= min;
      }
      const [min, max] = p.range.split('-').map((n) => parseInt(n, 10));
      return qty >= min && qty <= max;
    });
    return tier ?? gradeAProduct.pricing[0];
  };

  const currentPricing = getCurrentPricing(quantity);
  const totalPrice = currentPricing.price * quantity;

  // -----------------------------------------------------------------------
  //  Scrolling helpers (used by multiple CTA buttons)
  // -----------------------------------------------------------------------
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // -----------------------------------------------------------------------
  //  Navigation links – use translation keys
  // -----------------------------------------------------------------------
  const navLinks = [
    { labelKey: 'nav_used_pallets', href: '/#used-pallets' },
    { labelKey: 'nav_new_pallets', href: '/#new-pallets' },
    { labelKey: 'nav_services', href: '/#services' },
    { labelKey: 'nav_about', href: '/#about' },
    { labelKey: 'nav_faq', href: '/#faq' },
    { labelKey: 'nav_contact', href: '/#contact' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-gray-800 font-sans">
      {/* ── NAVBAR ── */}
      <nav
        className={`fixed w-full z-50 transition-all duration-400 ${
          scrolled
            ? 'bg-white/98 backdrop-blur-md shadow-lg'
            : 'bg-white shadow-md'
        }`}
      >
        <div className="px-6 lg:px-16 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="h-20 w-20 flex items-center justify-center">
              <img
                src={LOGO_URL}
                alt="PEL Partner Logo"
                className="h-20 w-20 object-contain drop-shadow-lg"
              />
            </div>
            <span className="text-3xl font-bold tracking-widest uppercase text-gray-900">
              PEL Partner
            </span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((item) => (
              <a
                key={item.labelKey}
                href={item.href}
                className="text-sm tracking-widest uppercase transition-colors whitespace-nowrap cursor-pointer font-medium text-gray-600 hover:text-gray-900"
              >
                {t(item.labelKey)}
              </a>
            ))}
            <a
              href="/#contact"
              className="px-5 py-2 bg-yellow-500 text-black text-sm font-bold rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase shadow-md"
            >
              {t('nav_get_quote')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl text-gray-900`}></i>
          </button>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-6 pb-6 flex flex-col gap-4">
            {navLinks.map((item) => (
              <a
                key={item.labelKey}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="text-gray-700 hover:text-gray-900 uppercase tracking-widest text-sm cursor-pointer font-medium"
              >
                {t(item.labelKey)}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* ── BREADCRUMB ── */}
      <section className="pt-32 pb-6 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-yellow-600 transition-colors cursor-pointer">
              Home
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-400">Products</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-bold">Grade A Pallets</span>
          </nav>
        </div>
      </section>

      {/* ── HERO SECTION ── */}
      <section className="py-16 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Textual intro */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 rounded-full text-black text-xs tracking-widest uppercase mb-6 font-black">
                <i className="ri-star-fill"></i> PREMIUM QUALITY
              </div>
              <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
                Grade A
                <br />
                <span className="text-yellow-600">Used Euro Pallets</span>
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Our Grade A used Euro pallets represent the highest quality in the
                second‑hand pallet market. Each pallet is individually
                hand‑inspected by our trained staff to ensure it meets strict
                EPAL standards.
              </p>

              {/* Badges */}
              <div className="flex flex-wrap gap-4 mb-8">
                {gradeAProduct.qualityBadges.map((badge, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-full px-4 py-2"
                  >
                    <i className={`${badge.icon} text-yellow-600`}></i>
                    <span className="text-sm font-bold text-gray-700">
                      {badge.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowOrderModal(true)}
                  className="px-8 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>Order Now
                </button>
                <button
                  onClick={scrollToContact}
                  className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase"
                >
                  Request Quote
                </button>
              </div>
            </div>

            {/* Image gallery */}
            <div className="relative">
              <div
                className="relative rounded-xl overflow-hidden border border-gray-200 shadow-lg"
                style={{ aspectRatio: '4/3' }}
              >
                <img
                  src={gradeAProduct.detailImages[activeImg]}
                  alt="Grade A Pallet"
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase bg-yellow-500 text-black shadow-md">
                  PREMIUM
                </span>

                {/* Watermark */}
                <div className="absolute bottom-4 right-3 flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-md px-2 py-1">
                  <img src={LOGO_URL} alt="PEL Partner" className="h-5 w-5 object-contain opacity-90" />
                  <span className="text-white text-[10px] font-black tracking-widest uppercase opacity-90">
                    PEL Partner
                  </span>
                </div>
              </div>

              {/* Thumbnail selector */}
              <div className="flex gap-3 mt-4">
                {gradeAProduct.detailImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`flex-1 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      activeImg === i
                        ? 'border-yellow-500 shadow-md'
                        : 'border-gray-200 hover:border-yellow-400'
                    }`}
                    style={{ aspectRatio: '4/3' }}
                  >
                    <img
                      src={img}
                      alt={`View ${i + 1}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING SECTION ── */}
      <section className="py-20 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">
              COMPETITIVE PRICING
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Volume Pricing Tiers
            </h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">
              Order more and save! Our volume pricing ensures you get the best
              value for your business.
            </p>
          </div>

          {/* Tier cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {gradeAProduct.pricing.map((tier, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl border-2 p-6 transition-all ${
                  currentPricing.range === tier.range
                    ? 'border-yellow-500 shadow-lg'
                    : 'border-gray-200 hover:border-yellow-400'
                }`}
              >
                <div className="text-center">
                  <div className="text-gray-600 text-sm mb-2">{tier.range} pallets</div>
                  <div
                    className={`text-4xl font-black mb-2 ${
                      currentPricing.range === tier.range
                        ? 'text-yellow-600'
                        : 'text-gray-900'
                    }`}
                  >
                    €{tier.price}
                  </div>
                  <div className="text-gray-500 text-xs">per pallet</div>
                  {currentPricing.range === tier.range && (
                    <div className="mt-4 flex items-center justify-center gap-2 text-yellow-600 text-sm font-bold">
                      <i className="ri-check-line"></i>
                      <span>{t('calc_current_selection')}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quantity selector & total */}
          <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
            <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">
              {t('calc_calculate')}
            </h3>

            {/* Quantity controls */}
            <div className="flex items-center justify-center gap-6 mb-6">
              <div className="flex items-center border border-gray-300 rounded-md bg-white">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                >
                  <i className="ri-subtract-line text-xl"></i>
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="w-32 px-4 py-3 text-center border-l border-r border-gray-300 focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all text-lg font-bold"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                >
                  <i className="ri-add-line text-xl"></i>
                </button>
              </div>
            </div>

            {/* Total price */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-700 font-bold">{t('calc_total_price_label')}:</span>
                <span className="text-4xl font-black text-gray-900">
                  €{totalPrice.toFixed(2)}
                </span>
              </div>
              <div className="text-gray-600 text-sm text-right">
                ({quantity} {t('calc_pallets')} × €{currentPricing.price})
              </div>
            </div>

            {/* Payment methods */}
            <div className="mb-6">
              <h4 className="text-gray-900 text-sm font-black mb-3 text-center">
                {t('calc_payment_methods')}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-bank-card-line', label: 'Card Payment' },
                  { icon: 'ri-google-fill', label: 'Google Pay' },
                  { icon: 'ri-paypal-fill', label: 'PayPal' },
                  { icon: 'ri-bank-line', label: 'Bancontact' },
                ].map((p, idx) => (
                  <button
                    key={idx}
                    onClick={scrollToContact}
                    className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-4 py-3 hover:border-yellow-500 hover:bg-yellow-50 transition-all cursor-pointer"
                  >
                    <i className={`${p.icon} text-blue-600 text-xl`}></i>
                    <span className="text-sm font-bold text-gray-700">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Order button */}
            <button
              onClick={() => setShowOrderModal(true)}
              className="w-full py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md"
            >
              <i className="ri-shopping-cart-line mr-2"></i>
              Order €{totalPrice.toFixed(2)} Now
            </button>
          </div>
        </div>
      </section>

      {/* ── TECHNICAL SPECS ── */}
      <section className="py-20 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">
              SPECIFICATIONS
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Technical Details
            </h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {gradeAProduct.specs.map((spec, i) => (
              <div key={i} className="bg-gray-50 rounded-lg px-4 py-4 border border-gray-200">
                <div className="text-gray-500 text-xs mb-1 tracking-widest uppercase">
                  {spec.label}
                </div>
                <div className="text-gray-900 text-sm font-bold">{spec.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── KEY FEATURES ── */}
      <section className="py-20 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">
              QUALITY ASSURANCE
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Key Features
            </h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {gradeAProduct.features.map((feature, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:border-yellow-400 transition-all shadow-sm"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-50 rounded-full border border-yellow-200 mb-4">
                  <i className="ri-checkbox-circle-fill text-yellow-600 text-xl"></i>
                </div>
                <h4 className="text-gray-900 font-bold mb-2">{feature}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RELATED PRODUCTS ── */}
      <section className="py-20 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-yellow-600 text-xs tracking-widest uppercase mb-3 font-bold">
              EXPLORE MORE
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Other Pallet Options
            </h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Grade B */}
            <Link
              to="/pallets/grade-b"
              className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer"
            >
              <div className="relative h-60 w-full overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/6203e656-81f6-4e73-888e-80c3b1d5925f.png"
                  alt="Grade B Pallets"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase bg-amber-700 text-yellow-100 shadow-md">
                  VALUE
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-gray-900 mb-2">
                  Grade B Used Euro Pallets
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Cost-effective solution for standard operations. From €6/pallet.
                </p>
                <div className="flex items-center gap-2 text-yellow-600 font-bold text-sm">
                  <span>View Details</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </Link>

            {/* New pallets */}
            <Link
              to="/pallets/new"
              className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer"
            >
              <div className="relative h-60 w-full overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png"
                  alt="New Pallets"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase bg-green-700 text-yellow-100 shadow-md">
                  NEW
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-gray-900 mb-2">
                  New Wooden Pallets
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  ISPM15 certified, export ready. From €10/pallet.
                </p>
                <div className="flex items-center gap-2 text-yellow-600 font-bold text-sm">
                  <span>View Details</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ── CONTACT SECTION ── */}
      <section id="contact" className="py-20 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-black text-gray-900 mb-6">Ready to Order?</h2>
          <p className="text-gray-600 text-lg mb-8">
            Contact us today for a custom quote or to place your order.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a
              href="tel:+32487261970"
              className="px-8 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md"
            >
              <i className="ri-phone-line mr-2"></i>Call +32 487 26 19 70
            </a>
            <a
              href="mailto:pelpartnercompany@gmail.com"
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-yellow-500 hover:bg-yellow-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase"
            >
              <i className="ri-mail-line mr-2"></i>Email Us
            </a>
          </div>

          {/* Banking information */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-left">
            <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">
              Banking Information
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {/* EUR Account */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                    <span className="text-blue-600 text-xs font-black">€</span>
                  </div>
                  <span className="text-gray-900 font-black text-base">Euro Account</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-gray-500 text-xs mb-1">EUR IBAN</div>
                    <div className="text-gray-900 font-bold font-mono text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200">
                      LT71 3250 0690 5068 8936
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">BIC/SWIFT</div>
                    <div className="text-gray-900 font-bold font-mono text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200">
                      REVOLT21
                    </div>
                  </div>
                </div>
              </div>

              {/* GBP Account */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-full">
                    <span className="text-purple-600 text-xs font-black">£</span>
                  </div>
                  <span className="text-gray-900 font-black text-base">
                    British Pound Account
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Sort Code</div>
                    <div className="text-gray-900 font-bold font-mono text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200">
                      23-01-20
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Account Number</div>
                    <div className="text-gray-900 font-bold font-mono text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200">
                      87899767
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#2C2C2C] border-t border-gray-700 py-14 px-6 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            {/* Company info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="PEL Partner Logo" className="h-10 w-10 object-contain" />
                <span className="text-xl font-black tracking-widest text-white uppercase">
                  PEL Partner
                </span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
                {t('footer_desc')}
              </p>
            </div>

            {/* Product links */}
            <div>
              <h4 className="text-white font-black text-xs tracking-widest uppercase mb-5">
                {t('footer_products')}
              </h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li>
                  <Link to="/pallets/grade-a" className="hover:text-yellow-400 transition-colors cursor-pointer">
                    {t('form_product_a')}
                  </Link>
                </li>
                <li>
                  <Link to="/pallets/grade-b" className="hover:text-yellow-400 transition-colors cursor-pointer">
                    {t('form_product_b')}
                  </Link>
                </li>
                <li>
                  <Link to="/pallets/new" className="hover:text-yellow-400 transition-colors cursor-pointer">
                    {t('form_product_new')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-black text-xs tracking-widest uppercase mb-5">
                {t('footer_contact')}
              </h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-yellow-500"></i> Antwerp, Belgium
                </li>
                <li>
                  <a href="tel:+32487261970" className="flex items-center gap-2 hover:text-yellow-400 transition-colors cursor-pointer">
                    <i className="ri-phone-line text-yellow-500"></i> +32 487 26 19 70
                  </a>
                </li>
                <li>
                  <a href="mailto:pelpartnercompany@gmail.com" className="flex items-center gap-2 hover:text-yellow-400 transition-colors cursor-pointer break-all">
                    <i className="ri-mail-line text-yellow-500"></i> pelpartnercompany@gmail.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs">{t('footer_copyright')}</p>
            <p className="text-gray-500 text-xs">{t('footer_tagline')}</p>
          </div>
        </div>
      </footer>

      {/* ── WHATSAPP BUTTON ── */}
      <a
        href="https://wa.me/32487261970"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-full shadow-2xl hover:scale-110 transition-transform cursor-pointer"
        aria-label="Contact us on WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>

      {showOrderModal && <OrderModal onClose={() => setShowOrderModal(false)} />}
    </div>
  );
}
